// @flow
import React from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import SocialIcon from 'shared/components/SocialIcon';
import translations from 'shared/strings';
import { getEventDateInfo, getEventDateInfoCN } from 'shared/lib/dateUtils';
import { getWebDomain } from 'shared/lib/clientSettings';
import Icon from '@ff/ui/Icon';
import {
  FONT_FAMILY_ROBOTO
} from 'shared/constants';

type Event = {
  url: string,
  region: string,
  title: string,
  start_date: {
    date: string
  },
  end_date: {
    date: string
  },
  event_name: string,
  event_location: string,
  background_image_url: string,
  background_position: string,
  background_scale: string,
  event_description: string,
}

type Props = {
  classes: Object,
  event: Event,
}

class EventPostCard extends React.Component {
  props: Props = this.props;

  state = {
    expanded: false
  };

  render() {
    const {
      classes: c, event
    } = this.props;
    const { expanded } = this.state;
    const iconStyle = {
      fontSize: '20px',
      color: 'black',
      marginLeft: '8px',
      marginRight: '8px',
      textDecoration: 'none',
      cursor: 'pointer'
    };
    const timezone = (event.region === 'CN') ? 'Asia/Shanghai' : 'America/Los_Angeles';

    const dateInfo = event.region === 'CN' ?
      getEventDateInfoCN(event.start_date.date, event.end_date.date, timezone) :
      getEventDateInfo(event.start_date.date, event.end_date.date, timezone);

    const appDomain = getWebDomain(event.region);

    const {
      startDate,
      startTime,
      endDate,
      endTime,
      isSameDay
    } = dateInfo;

    const eventTime = `${startTime} - ${endTime}`;
    const eventDate = isSameDay ?
      `${startDate}` :
      `${startDate} ${translations.TO[event.region]}\n${endDate}`;

    return (
      <div className={c.cardArticle}>
        <a
          className={c.imageLink}
          href={event.url}
        >
          <div
            className={c.cardHeroImg}
            style={{
              backgroundImage: `url(${event.background_image_url})`

              /*
              Save for future use,
              uncomment these two lines if user want to make post card image
              display the same way as the related page.
              */
              // backgroundPosition: background_position,
              // backgroundSize: background_scale
            }}
          />
        </a>
        <div className={c.cardContent}>
          <div className={c.cardTitle}>
            <a href={event.url}>{event.event_name}</a>
          </div>
          <div className={c.cardDate}>
            <i className="material-icons">calendar_today</i>
            <div className={c.timeWrapper}>
              {eventDate.split('\n').map((date, i) => <p key={i} className={c.date}>{date}</p>)}
              <p className={c.time}>{eventTime}</p>
            </div>
          </div>
          <div className={c.cardDesc}>
            <i className="material-icons">location_on</i>
            <span>{event.event_location}</span>
          </div>
          {
            event.event_description &&
            <div className={c.cardDesc}>
              <p className={c.description}>{event.event_description}</p>
            </div>
          }
          <div>
            <a href={event.url} className={c.cardReadMore}>
              {translations.EVENT_DETAILS[event.region]}
            </a>
          </div>
        </div>
        <div className={c.cardSocialShare}>
          <Icon
            name="ff-icon-share"
            className={c.socialIcon}
            onMouseEnter={() => this.setState({ expanded: true })}
            onMouseLeave={() => this.setState({ expanded: false })}
          />
          <div
            className={classNames(c.iconList, expanded && c.expanded)}
            onMouseEnter={() => this.setState({ expanded: true })}
            onMouseLeave={() => this.setState({ expanded: false })}
          >
            {event.social_share.map(link => (
              <SocialIcon
                key={`card${link.url}`}
                iconStyle={iconStyle}
                link={link}
                generateQRCode={`${appDomain}${event.url}`}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }
}

const styles = {
  cardArticle: {
    backgroundColor: 'white',
    marginTop: '36px',
    marginBottom: '36px',
    paddingBottom: '36px',
    width: '100%',
    fontFamily: FONT_FAMILY_ROBOTO,
    '@media(min-width: 768px)': {
      width: '45%',
      marginRight: '5%'
    },
    '@media(min-width: 1037px)': {
      width: '30%',
      marginRight: '3.3%'
    },
    '@media(max-width: 960px)': {
      width: '45%'
    },
    '@media(max-width: 768px)': {
      width: '100%',
      paddingLeft: '0',
      paddingRight: '0',
      paddingBottom: '0',
      marginBottom: '0'
    }
  },
  cardHeroImg: {
    width: '100%',
    height: '20vh',
    maxHeight: '194px',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center center'
  },
  imageLink: {
    height: '100%',
    width: '100%'
  },
  cardContent: {
    padding: '18px 0',
    marginLeft: '36px',
    marginRight: '36px',
    borderBottom: '1px solid #eaeaea',
    '@media(max-width: 768px)': {
      paddingLeft: '18px',
      paddingRight: '18px'
    }
  },
  cardDate: {
    fontSize: '12px',
    letterSpacing: '1.2px',
    lineHeight: '1.75em',
    padding: '18px 0',
    textTransform: 'uppercase',
    display: 'flex',
    '& > i': {
      fontSize: '16px',
      paddingRight: '5px',
      paddingTop: '1px'
    }
  },
  timeWrapper: {
    '& > p': {
      margin: 0
    }
  },
  date: {
    fontWeight: 500
  },
  time: {
    fontWeight: 300
  },
  cardTitle: {
    fontSize: '23px',
    fontWeight: '300',
    letterSpacing: '.025em',
    lineHeight: '1.5em',
    paddingBottom: '18px',
    cursor: 'pointer',
    transition: 'all .2s ease-in',
    '& > a': {
      color: 'black',
      textDecoration: 'none'
    },
  },
  cardSocialShare: {
    paddingLeft: ' 36px',
    paddingTop: '18px',
    display: 'flex',
    alignItems: 'center'
  },
  iconList: {
    marginLeft: '10px',
    width: '0',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    transition: 'all .4s ease'
  },
  expanded: {
    width: '100%'
  },
  socialIcon: {
    fontSize: '15px',
    linegHeight: '1.2em',
    cursor: 'pointer'
  },
  cardDesc: {
    paddingBottom: '18px',
    fontSize: '12px',
    lineHeight: '1.75em',
    '& > i': {
      fontSize: '18px',
      verticalAlign: 'middle',
      paddingRight: '5px'
    },
    '& > span': {
      fontWeight: 500
    }
  },
  description: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': 2
  },
  cardReadMore: {
    paddingBottom: '18px',
    fontSize: '12px',
    textDecoration: 'none',
    fontWeight: '600',
    marginRight: '25px',
    letterSpacing: '.1em',
    lineHeight: '1.75em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    color: 'black',
    '&:hover': {
      color: 'hsl(0, 0%, 65%)'
    }
  }
};

export default injectSheet(styles)(EventPostCard);
